import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicEPK, getFileUrl } from '../lib/api';
import { Music, MapPin, Disc, Tag, Calendar, ExternalLink } from 'lucide-react';

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function extractSpotifyUri(url) {
  if (!url) return null;
  // Handle track, album, playlist URLs
  const match = url.match(/open\.spotify\.com\/(track|album|playlist)\/([a-zA-Z0-9]+)/);
  if (match) return `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=generator&theme=0`;
  return null;
}

export default function EPKViewer() {
  const { slug } = useParams();
  const [epk, setEpk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getPublicEPK(slug)
      .then(({ data }) => { setEpk(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="epk-viewer flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--epk-primary)] border-t-transparent animate-spin mx-auto mb-4" />
          <p style={{ color: 'var(--epk-text-muted)' }}>LADEN...</p>
        </div>
      </div>
    );
  }

  if (error || !epk) {
    return (
      <div className="epk-viewer flex items-center justify-center min-h-screen">
        <div className="text-center" data-testid="epk-not-found">
          <h1 className="text-4xl font-black mb-4" style={{ color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>404</h1>
          <p style={{ color: 'var(--epk-text-muted)' }}>EPK nicht gefunden</p>
        </div>
      </div>
    );
  }

  const { basic_info, short_bio, musical_background, releases_strategy, releases, youtube_videos, gallery_images, stats, social_links } = epk;

  return (
    <div className="epk-viewer" data-testid="epk-viewer">
      {/* HERO */}
      <section className="epk-hero" data-testid="epk-hero">
        {gallery_images && gallery_images.length > 0 && (
          <div className="absolute inset-0">
            <img
              src={getFileUrl(gallery_images[0].storage_path)}
              alt={basic_info?.artist_name}
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          </div>
        )}
        <div className="relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>ELECTRONIC PRESS KIT</p>
          <h1 data-testid="epk-artist-name">{basic_info?.artist_name}</h1>
          <div className="flex flex-wrap gap-6 mt-6 text-sm" style={{ color: 'var(--epk-text-muted)' }}>
            {basic_info?.genre && <span className="flex items-center gap-2"><Music size={14} style={{ color: 'var(--epk-primary)' }} /> {basic_info.genre}</span>}
            {basic_info?.origin && <span className="flex items-center gap-2"><MapPin size={14} style={{ color: 'var(--epk-primary)' }} /> {basic_info.origin}</span>}
            {basic_info?.label && <span className="flex items-center gap-2"><Tag size={14} style={{ color: 'var(--epk-primary)' }} /> {basic_info.label}</span>}
          </div>
          {(basic_info?.next_release || basic_info?.last_release) && (
            <div className="flex flex-wrap gap-6 mt-4 text-sm">
              {basic_info?.next_release && (
                <span className="flex items-center gap-2" style={{ color: 'var(--epk-primary)' }}>
                  <Calendar size={14} /> NÄCHSTES RELEASE: {basic_info.next_release}
                </span>
              )}
              {basic_info?.last_release && (
                <span className="flex items-center gap-2" style={{ color: 'var(--epk-text-muted)' }}>
                  <Disc size={14} /> LETZTES RELEASE: {basic_info.last_release}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* SHORT BIO */}
      {short_bio && (
        <section className="epk-section" data-testid="epk-bio-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">KURZBIOGRAFIE</h2>
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--epk-text)', maxWidth: '65ch' }}>{short_bio}</p>
          </div>
        </section>
      )}

      {/* MUSICAL BACKGROUND */}
      {musical_background && (
        <section className="epk-section" data-testid="epk-musical-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">MUSIKALISCHER WERDEGANG</h2>
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--epk-text)', maxWidth: '65ch' }}>{musical_background}</p>
          </div>
        </section>
      )}

      {/* RELEASES STRATEGY */}
      {releases_strategy && (
        <section className="epk-section" data-testid="epk-strategy-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">RELEASES & STRATEGIE</h2>
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--epk-text)', maxWidth: '65ch' }}>{releases_strategy}</p>
          </div>
        </section>
      )}

      {/* RELEASES */}
      {releases && releases.length > 0 && (
        <section className="epk-section" data-testid="epk-releases-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">RELEASES</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {releases.map((rel, idx) => {
                const spotifyEmbed = extractSpotifyUri(rel.spotify_embed_url);
                return (
                  <div key={rel.id || idx} className="release-card" data-testid={`epk-release-${idx}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold" style={{ color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>{rel.title}</h3>
                        <p className="text-xs mt-1" style={{ color: 'var(--epk-text-muted)' }}>
                          {rel.date} &mdash; {rel.format} &mdash; {rel.producer}
                        </p>
                      </div>
                      <span
                        data-testid={`epk-release-status-${idx}`}
                        className="shrink-0 px-3 py-1 text-xs font-bold uppercase tracking-wider"
                        style={{
                          fontFamily: 'var(--font-heading)',
                          background: rel.is_released !== false ? 'var(--epk-primary)' : 'transparent',
                          color: rel.is_released !== false ? '#000' : 'var(--epk-primary)',
                          border: rel.is_released !== false ? 'none' : '1px solid var(--epk-primary)',
                        }}
                      >
                        {rel.is_released !== false ? 'RELEASED' : 'UPCOMING'}
                      </span>
                    </div>
                    {rel.description && <p className="text-sm mb-4" style={{ color: 'var(--epk-text)' }}>{rel.description}</p>}
                    {spotifyEmbed && (
                      <div className="spotify-embed mb-3">
                        <iframe
                          src={spotifyEmbed}
                          width="100%"
                          height="152"
                          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                          loading="lazy"
                          title={`Spotify: ${rel.title}`}
                          style={{ border: 'none' }}
                        />
                      </div>
                    )}
                    {rel.link && (
                      <a href={rel.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: 'var(--epk-primary)' }}>
                        <ExternalLink size={14} /> ANHÖREN
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* YOUTUBE VIDEOS */}
      {youtube_videos && youtube_videos.filter(v => extractYouTubeId(v)).length > 0 && (
        <section className="epk-section" data-testid="epk-youtube-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">VIDEOS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {youtube_videos.filter(v => extractYouTubeId(v)).map((url, idx) => (
                <div key={idx} className="youtube-embed" data-testid={`epk-youtube-${idx}`}>
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(url)}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`Video ${idx + 1}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {gallery_images && gallery_images.length > 1 && (
        <section className="epk-section px-0" data-testid="epk-gallery-section">
          <div className="max-w-4xl mx-auto px-8 mb-6">
            <h2 className="epk-section-title">GALERIE</h2>
          </div>
          <div className="gallery-grid">
            {gallery_images.map((img, idx) => (
              <img
                key={img.id}
                src={getFileUrl(img.storage_path)}
                alt={img.original_filename || `Bild ${idx + 1}`}
                data-testid={`epk-gallery-img-${idx}`}
                className={idx % 3 === 0 ? 'col-span-2' : ''}
                style={{ height: idx % 3 === 0 ? '400px' : '300px' }}
              />
            ))}
          </div>
        </section>
      )}

      {/* STATS */}
      {stats && stats.filter(s => s.value).length > 0 && (
        <section className="epk-section" data-testid="epk-stats-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">DATEN & FAKTEN</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {stats.filter(s => s.value).map((stat, idx) => (
                <div key={idx} data-testid={`epk-stat-${idx}`}>
                  <div className="epk-stat-value">{stat.value}</div>
                  <div className="epk-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SOCIAL & CONTACT */}
      {social_links && (
        <section className="epk-section" data-testid="epk-social-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="epk-section-title">SOCIAL MEDIA & KONTAKT</h2>
            <div className="flex flex-wrap gap-4">
              {social_links.instagram && (
                <a href={social_links.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="px-6 py-3 border text-sm font-bold uppercase tracking-wider hover:bg-[var(--epk-primary)] hover:text-black transition-colors" style={{ borderColor: 'var(--epk-primary)', color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>
                  INSTAGRAM
                </a>
              )}
              {social_links.youtube && (
                <a href={social_links.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="px-6 py-3 border text-sm font-bold uppercase tracking-wider hover:bg-[var(--epk-primary)] hover:text-black transition-colors" style={{ borderColor: 'var(--epk-primary)', color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>
                  YOUTUBE
                </a>
              )}
              {social_links.spotify && (
                <a href={social_links.spotify} target="_blank" rel="noopener noreferrer" data-testid="social-spotify" className="px-6 py-3 border text-sm font-bold uppercase tracking-wider hover:bg-[var(--epk-primary)] hover:text-black transition-colors" style={{ borderColor: 'var(--epk-primary)', color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>
                  SPOTIFY
                </a>
              )}
              {social_links.apple_music && (
                <a href={social_links.apple_music} target="_blank" rel="noopener noreferrer" data-testid="social-apple-music" className="px-6 py-3 border text-sm font-bold uppercase tracking-wider hover:bg-[var(--epk-primary)] hover:text-black transition-colors" style={{ borderColor: 'var(--epk-primary)', color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>
                  APPLE MUSIC
                </a>
              )}
              {social_links.email && (
                <a href={`mailto:${social_links.email}`} data-testid="social-email" className="px-6 py-3 border text-sm font-bold uppercase tracking-wider hover:bg-[var(--epk-primary)] hover:text-black transition-colors" style={{ borderColor: 'var(--epk-primary)', color: 'var(--epk-primary)', fontFamily: 'var(--font-heading)' }}>
                  E-MAIL
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="py-8 px-6 border-t" style={{ borderColor: 'rgba(255,232,0,0.1)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src="/AC_logo_longbeige.png" alt="ALL CAPS" className="h-5 opacity-50" />
          <p className="text-xs" style={{ color: 'var(--epk-text-muted)' }}>ELECTRONIC PRESS KIT</p>
        </div>
      </footer>
    </div>
  );
}
